import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Returns an element's child-index path relative to a root element.
 * Runtime-injected widgets can use this to return to the equivalent server-rendered parent.
 * @param {Element} root
 * @param {Element} element
 * @returns {number[] | null}
 */
function getElementPath(root, element) {
  const path = [];
  let current = element;

  while (current !== root) {
    const parent = current.parentElement;
    if (!parent) return null;

    const index = Array.from(parent.children).indexOf(current);
    if (index < 0) return null;

    path.unshift(index);
    current = parent;
  }

  return path;
}

/**
 * Finds an element in a replacement tree using a path from getElementPath.
 * @param {Element} root
 * @param {number[] | null} path
 * @returns {Element | null}
 */
function getElementAtPath(root, path) {
  if (!path) return null;

  let current = root;
  for (const index of path) {
    const child = current.children[index];
    if (!(child instanceof Element)) return null;
    current = child;
  }

  return current;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        /*
         * Wishlist by Square injects its product-page heart into the live media
         * gallery after page load. Its product-page wrapper is not guaranteed to
         * use the collection-card class, so find the top-level Vtl-Wishlist node
         * rather than relying on one exact wrapper name. Moving the live node
         * preserves the app-owned state and click listeners.
         */
        const wishlistElements = Array.from(this.querySelectorAll('[class*="Vtl-Wishlist"]'));
        const wishlistWidgets = wishlistElements
          .filter((element) => !element.parentElement?.closest('[class*="Vtl-Wishlist"]'))
          .map((widget) => ({ widget, parentPath: getElementPath(this, widget.parentElement ?? this) }));

        for (const { widget, parentPath } of wishlistWidgets) {
          const newWidgetParent = getElementAtPath(newMediaGallery, parentPath) ?? newMediaGallery;
          newWidgetParent.append(widget);
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
